import { useEffect, useState } from 'react';
import apiClient from '../../../services/api';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';

type ReportEmailType = 'brief' | 'field_health';
type ReportEmailFrequency = 'weekly' | 'monthly';

function statusFromError(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
}

type Subscription = {
  id: string;
  farm_id: string;
  report_type: ReportEmailType;
  frequency: ReportEmailFrequency | 'daily';
  send_hour: number;
  enabled: boolean;
  recipient_email: string;
};

export default function FarmReportEmailSchedule({
  farmId,
  farmName,
  labelClass,
  variant = 'panel',
}: {
  farmId?: string | number | null;
  farmName?: string | null;
  labelClass: string;
  /** Popup locks this to the farm brief and uses shorter wording. */
  variant?: 'panel' | 'popup';
}) {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const id = String(farmId ?? '').trim();
  const canSchedule = /^\d+$/.test(id);
  const [reportType, setReportType] = useState<ReportEmailType>('brief');
  const [frequency, setFrequency] = useState<ReportEmailFrequency>('weekly');
  const [email, setEmail] = useState(user?.email || '');
  const [enabled, setEnabled] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'send' | 'remove' | null>(null);

  useEffect(() => {
    if (!email && user?.email) setEmail(user.email);
  }, [email, user?.email]);

  useEffect(() => {
    if (!canSchedule) return;
    let cancelled = false;
    apiClient
      .get<Subscription[]>('/farm-report-subscriptions/', { params: { farm_id: id } })
      .then((res) => {
        if (cancelled) return;
        const match = (res.data || []).find((row) => row.report_type === reportType);
        if (!match) {
          setSubscriptionId(null);
          setEnabled(true);
          return;
        }
        setSubscriptionId(match.id);
        setFrequency(match.frequency === 'monthly' ? 'monthly' : 'weekly');
        setEmail(match.recipient_email || user?.email || '');
        setEnabled(match.enabled);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canSchedule, id, reportType, user?.email]);

  const inputClass = `w-full rounded-[8px] border px-2 py-1.5 text-xs ${
    isDark ? 'border-white/10 bg-[#161b22] text-[#e6edf3]' : 'border-[#e5e7eb] bg-white text-[#111827]'
  }`;
  const lockedToBrief = variant === 'popup';
  const activeType: ReportEmailType = lockedToBrief ? 'brief' : reportType;

  const save = async () => {
    if (!canSchedule || !email.trim()) return;
    setBusy('save');
    setStatus(null);
    try {
      const res = await apiClient.put<Subscription>('/farm-report-subscriptions/', {
        farm_id: id,
        farm_name: farmName || null,
        report_type: activeType,
        frequency,
        enabled,
        recipient_email: email.trim(),
      });
      setSubscriptionId(res.data.id);
      setFrequency(res.data.frequency === 'monthly' ? 'monthly' : 'weekly');
      setStatus(enabled ? 'Email schedule saved.' : 'Schedule paused.');
    } catch (error) {
      console.error('Save farm report email failed:', error);
      setStatus(statusFromError(error, 'Could not save the email schedule.'));
    } finally {
      setBusy(null);
    }
  };

  const sendNow = async () => {
    if (!canSchedule || !email.trim()) return;
    setBusy('send');
    setStatus(null);
    try {
      const res = await apiClient.post<{ message?: string }>('/farm-report-subscriptions/send-now/', {
        farm_id: id,
        farm_name: farmName || null,
        report_type: activeType,
        recipient_email: email.trim(),
      });
      setStatus(res.data?.message || 'Test email sent.');
    } catch (error) {
      console.error('Send farm report email failed:', error);
      setStatus(statusFromError(error, 'Could not send the test email.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!subscriptionId) return;
    setBusy('remove');
    try {
      await apiClient.delete(`/farm-report-subscriptions/${subscriptionId}/`);
      setSubscriptionId(null);
      setStatus('Email schedule removed.');
    } catch (error) {
      console.error('Remove farm report email failed:', error);
      setStatus(statusFromError(error, 'Could not remove the email schedule.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className={
        lockedToBrief
          ? `mt-5 border-t pt-4 ${isDark ? 'border-white/10' : 'border-[#e5e7eb]'}`
          : `mt-4 rounded-[12px] border p-3 ${isDark ? 'border-white/10' : 'border-[#e5e7eb]'}`
      }
    >
      <p className={`text-xs font-medium ${labelClass}`}>{lockedToBrief ? 'Get it by email' : 'Email this farm'}</p>
      <p className={`mt-1 text-[11px] leading-snug ${isDark ? 'text-gray-400' : 'text-[#6b7280]'}`}>
        {lockedToBrief
          ? 'The email is the same PDF as the download: today’s weather, how the crop looks, dryness, pests, and what to do next. It is sent at 7:00 AM Philippines time, every Monday or on the 1st of the month.'
          : activeType === 'field_health'
            ? 'The email is a short note on how the crop looks right now. It is not the date-range file. Use Download for the full history. It is sent at 7:00 AM Philippines time, every Monday or on the 1st of the month.'
            : 'The email is the same PDF as Download report: today’s weather, how the crop looks, dryness, pests, and what to do next. It is sent at 7:00 AM Philippines time, every Monday or on the 1st of the month.'}
      </p>
      {!canSchedule ? (
        <p className="mt-2 text-[11px] opacity-70">Save the field first to schedule email.</p>
      ) : (
        <div className="mt-2 space-y-2">
          {!lockedToBrief && (
            <select value={reportType} onChange={(e) => setReportType(e.target.value as ReportEmailType)} className={inputClass}>
              <option value="brief">Regular Farm Brief</option>
              <option value="field_health">Field Health — change over time</option>
            </select>
          )}
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as ReportEmailFrequency)}
            className={inputClass}
          >
            <option value="weekly">Every Monday</option>
            <option value="monthly">First of the month</option>
          </select>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className={inputClass}
          />
          <label className="flex items-center gap-2 text-[11px]">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Send it on this schedule
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void sendNow()}
              disabled={busy !== null || !email.trim()}
              className={`rounded-[8px] border px-2 py-1.5 text-[11px] font-medium disabled:opacity-50 ${
                isDark ? 'border-white/15 text-[#e6edf3]' : 'border-[#008540] text-[#008540]'
              }`}
            >
              {busy === 'send' ? 'Sending…' : 'Send now'}
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy !== null || !email.trim()}
              className="flex-1 rounded-[8px] bg-[#008540] px-2 py-1.5 text-[11px] font-medium text-white disabled:opacity-50"
            >
              {busy === 'save' ? 'Saving…' : lockedToBrief ? 'Save schedule' : 'Save email schedule'}
            </button>
            {subscriptionId && (
              <button
                type="button"
                onClick={() => void remove()}
                disabled={busy !== null}
                className={`rounded-[8px] px-2 py-1.5 text-[11px] ${isDark ? 'text-red-300' : 'text-red-700'}`}
              >
                Remove
              </button>
            )}
          </div>
          {status && <p className="text-[11px]">{status}</p>}
        </div>
      )}
    </div>
  );
}
